import React from "react";
import { nudgeText } from "../../../core/data/auditsPage";
import { formatDate } from "../../../core/data/marketOverviewText";
import { INDUSTRY } from "../../../core/theme/auditsTheme";
import TargetRings from "./TargetRings";
import SearchesGraph from "./SearchesGraph";
import ChannelMixBar from "./ChannelMixBar";
import { TickIcon, LockIcon } from "./AuditIcons";

// Top left: the selected industry's Industry Audit progress. Rings, numbers
// and nudge come straight from the roll-up (nicheRollup); nothing is counted
// here. Unsorted and No niche never get rings: they don't count.
//   industry: from the list ({ name, nicheId, snapshots, special })
//   niche:    the roll-up's entry (nicheNumbers), or null (No niche, loading, failed)
//   rollupState: useNicheRollup's { status, rollup }
export default function IndustryAuditPanel({ industry, niche, rollupState }) {
  const ready = rollupState.status === "ready";

  return (
    <section aria-label="Industry Audit" className={`${INDUSTRY.panel} ${INDUSTRY.audit}`}>
      <div className={INDUSTRY.auditHead}>
        <div className="min-w-0">
          <div className={INDUSTRY.label}>{industry.special ? "Snapshots" : "Industry Audit"}</div>
          <h1 className={INDUSTRY.auditName}>{industry.name}</h1>
          {niche && niche.insightSince && (
            <div className={INDUSTRY.auditSince}>Insight since {formatDate(niche.insightSince)}{niche.estimated ? " (estimated)" : ""}</div>
          )}
        </div>
        {!industry.special && ready && niche && (niche.unlocked ? (
          <span className={INDUSTRY.pillUnlocked}>Unlocked</span>
        ) : (
          <span className={INDUSTRY.pillBuilding}><LockIcon className={INDUSTRY.pillLock} />Building</span>
        ))}
      </div>

      {!industry.nicheId ? (
        <SpecialNumbers industry={industry} niche={null} />
      ) : rollupState.status === "loading" ? (
        <p className={INDUSTRY.note}>Loading the Industry Audit numbers…</p>
      ) : !ready ? (
        <p className={INDUSTRY.warning}>The Industry Audit numbers couldn't be loaded. Your Snapshots are unaffected.</p>
      ) : industry.special ? (
        <SpecialNumbers industry={industry} niche={niche} />
      ) : (
        <NicheProgress niche={niche} rollup={rollupState.rollup} />
      )}
    </section>
  );
}

function NicheProgress({ niche, rollup }) {
  const nudge = nudgeText(niche);
  const est = niche.estimated ? "estimated" : "";
  // "679 / 1,000" with a tick, when the targets loaded.
  const goal = t => niche.progress && niche.progress[t];

  return (
    <>
      {!rollup.targetsLoaded && <p className={INDUSTRY.warning}>The unlock targets couldn't be loaded, so every niche shows as locked.</p>}
      {!rollup.complete && <p className={INDUSTRY.warning}>Some rows didn't load, so these numbers may be short.</p>}
      <div className={INDUSTRY.ringsRow}>
        <TargetRings progress={niche.progress} />
        <div className={INDUSTRY.stats}>
          <Stat swatch={0} label="Searches" value={niche.searches} goal={goal("searches")} sub={est} />
          <Stat swatch={1} label="Locations" value={niche.locations} goal={goal("locations")} sub={est} />
          <Stat swatch={2} label="Keywords" value={niche.keywords} goal={goal("keywords")} sub={est} />
          <Stat swatch={3} label="Advertisers found" value={niche.advertisers} sub="across every channel" />
        </div>
      </div>
      <div className={INDUSTRY.graphs}>
        <SearchesGraph points={niche.searchesOverTime} />
        <ChannelMixBar mix={niche.channelMix} />
      </div>
      <div className={INDUSTRY.nudgeRow}>
        <div className={INDUSTRY.nudge}>{nudge}</div>
        {/* The Industry Report page doesn't exist yet: unlocked, the button only says so. */}
        {niche.unlocked ? (
          <button type="button" className={INDUSTRY.report} title="The Industry Report is coming soon">View Industry Report</button>
        ) : (
          <button type="button" disabled className={INDUSTRY.reportLocked} title="Unlocks when all three targets are met">
            <LockIcon className={INDUSTRY.reportLock} />View Industry Report
          </button>
        )}
      </div>
    </>
  );
}

// Unsorted (the roll-up's numbers, no rings) and No niche (Snapshot count only).
function SpecialNumbers({ industry, niche }) {
  const est = niche && niche.estimated ? "estimated" : "";
  return (
    <>
      <div className={INDUSTRY.stats}>
        <Stat label="Snapshots" value={industry.snapshots} />
        {niche && (
          <>
            <Stat label="Searches" value={niche.searches} sub={est} />
            <Stat label="Locations" value={niche.locations} sub={est} />
            <Stat label="Keywords" value={niche.keywords} sub={est} />
            <Stat label="Advertisers found" value={niche.advertisers} sub="across every channel" />
          </>
        )}
      </div>
      <p className={INDUSTRY.note}>
        {industry.nicheId ? "Snapshots in Unsorted" : "Snapshots with no niche"} don't count towards an Industry Audit.
      </p>
    </>
  );
}

// goal: the target's { target, met } (shows "/ target" and a tick), or none.
function Stat({ swatch, label, value, goal, sub }) {
  return (
    <div>
      <div className={INDUSTRY.statLabel}>
        {swatch != null && <span aria-hidden="true" className={`w-2 h-2 rounded-full ${INDUSTRY.swatch[swatch]}`} />}
        {label}
      </div>
      <div className={INDUSTRY.statValueRow}>
        <span className={INDUSTRY.statValue}>
          {Number(value || 0).toLocaleString("en-GB")}
          {goal && <span className={INDUSTRY.statTarget}> / {goal.target.toLocaleString("en-GB")}</span>}
        </span>
        {goal && <TickIcon className={goal.met ? INDUSTRY.tickMet : INDUSTRY.tickNotMet} label={goal.met ? "Target met" : "Target not met yet"} />}
      </div>
      {sub && <div className={INDUSTRY.statSub}>{sub}</div>}
    </div>
  );
}
